import { useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { ptBR } from 'date-fns/locale';

/**
 * React Hook for Date/Time Formatting in LawTech PRO
 *
 * IMPORTANT: This hook handles all date/timezone formatting in the frontend.
 *
 * Core Principle: "Store in UTC, Display in Local"
 * - Backend sends dates in UTC
 * - This hook formats them for display in user's timezone
 * - Prepares dates for submission back to backend
 *
 * Usage Example:
 * ```tsx
 * const { formatDateOnly, formatDateTime, prepareDateForInput } = useDateTimeFormat();
 *
 * // Display date from backend
 * <p>Data de Nascimento: {formatDateOnly(client.birthDate)}</p>
 *
 * // Prepare date for HTML input
 * <input type="date" value={prepareDateForInput(client.birthDate)} />
 * ```
 *
 * @see TIMEZONE_STRATEGY.md for implementation details
 */
export function useDateTimeFormat() {
  // Get user's timezone from browser
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  /**
   * Formata uma date-only string (YYYY-MM-DD ou ISO com timezone) para exibição
   *
   * Evita conversão de timezone extraindo apenas a parte da data.
   * Perfeito para campos DATE (birth_date, incident_date, etc.)
   *
   * Examples:
   *   '1991-06-27'                  → '27/06/1991'
   *   '1991-06-27T12:00:00.000Z'   → '27/06/1991'
   *   null                         → '-'
   *   undefined                    → '-'
   *
   * @param dateString - Date string from backend (YYYY-MM-DD or ISO)
   * @returns Formatted date string for display (DD/MM/YYYY)
   */
  const formatDateOnly = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';

    try {
      // Extract just the date part (first 10 characters: YYYY-MM-DD)
      // This works for both '2024-01-15' and '2024-01-15T12:00:00.000Z'
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-');

      // Validate parts
      if (!year || !month || !day) {
        console.warn('Invalid date format:', dateString);
        return '-';
      }

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return '-';
    }
  };

  /**
   * Formata um ISO timestamp completo para exibição local
   *
   * Converte UTC timestamp para timezone local do usuário.
   * Perfeito para campos TIMESTAMPTZ (created_at, updated_at, etc.)
   *
   * Examples:
   *   '2024-01-15T18:30:00.000Z'  → '15/01/2024 15:30' (Brazil UTC-3)
   *   '2024-12-25T12:00:00.000Z'  → '25/12/2024 09:00' (Brazil UTC-3)
   *   null                        → '-'
   *
   * @param isoString - ISO timestamp string from backend
   * @returns Formatted datetime string for display (DD/MM/YYYY HH:mm)
   */
  const formatDateTime = (isoString: string | null | undefined): string => {
    if (!isoString) return '-';

    try {
      // Normalize PostgreSQL timestamp to ISO format
      // PostgreSQL returns: '2025-11-01 00:51:22.039469'
      // We need: '2025-11-01T00:51:22.039469Z'
      let normalizedString = isoString;
      if (isoString.includes(' ') && !isoString.includes('T')) {
        // Replace space with T and add Z suffix
        normalizedString = isoString.replace(' ', 'T') + 'Z';
      } else if (!isoString.endsWith('Z') && isoString.includes('T')) {
        // Has T but missing Z
        normalizedString = isoString + 'Z';
      }

      // Parse ISO string to Date object (UTC)
      const utcDate = parseISO(normalizedString);

      // Check if valid
      if (isNaN(utcDate.getTime())) {
        console.warn('Invalid datetime:', isoString);
        return '-';
      }

      // Format UTC date in user's timezone using formatInTimeZone
      const formatted = formatInTimeZone(
        utcDate,
        userTimezone,
        'dd/MM/yyyy HH:mm',
        { locale: ptBR }
      );

      return formatted;
    } catch (error) {
      console.error('Error formatting datetime:', isoString, error);
      return '-';
    }
  };

  /**
   * Formata timestamp para exibição curta (apenas data)
   *
   * Similar to formatDateTime but shows only the date part
   *
   * Example:
   *   '2024-01-15T18:30:00.000Z'  → '15/01/2024'
   *
   * @param isoString - ISO timestamp string from backend
   * @returns Formatted date string (DD/MM/YYYY)
   */
  const formatDateTimeShort = (isoString: string | null | undefined): string => {
    if (!isoString) return '-';

    try {
      // Parse ISO string to Date object (UTC)
      const utcDate = parseISO(isoString);

      if (isNaN(utcDate.getTime())) {
        console.warn('Invalid datetime:', isoString);
        return '-';
      }

      // Format UTC date in user's timezone using formatInTimeZone
      return formatInTimeZone(
        utcDate,
        userTimezone,
        'dd/MM/yyyy',
        { locale: ptBR }
      );
    } catch (error) {
      console.error('Error formatting datetime short:', isoString, error);
      return '-';
    }
  };

  /**
   * Prepara uma date-only string para envio ao backend
   *
   * HTML date inputs provide YYYY-MM-DD format.
   * This function ensures it stays in that format for backend processing.
   *
   * Examples:
   *   '1991-06-27'  → '1991-06-27'  (no change)
   *   ''            → null
   *   null          → null
   *
   * NOTE: Backend's DateTimeUtils.dateOnlyToUTC() will convert this to UTC noon
   *
   * @param dateString - Date from HTML input (YYYY-MM-DD)
   * @returns Date string ready for backend, or null
   */
  const prepareDateForSubmit = (dateString: string | null | undefined): string | null => {
    console.log('🔍 prepareDateForSubmit - INPUT:', dateString);

    if (!dateString || dateString.trim() === '') {
      console.log('🔍 prepareDateForSubmit - RETURNING NULL (empty input)');
      return null;
    }

    const result = dateString.trim();
    console.log('🔍 prepareDateForSubmit - OUTPUT:', result);

    // Already in YYYY-MM-DD format from HTML input
    // Backend will convert to UTC noon
    return result;
  };

  /**
   * Prepara date-only do backend para HTML input
   *
   * HTML date inputs require YYYY-MM-DD format.
   * This function extracts that from backend ISO strings.
   *
   * Examples:
   *   '1991-06-27T12:00:00.000Z'  → '1991-06-27'
   *   '1991-06-27'                → '1991-06-27'
   *   null                        → ''
   *   undefined                   → ''
   *
   * @param isoString - Date string from backend (ISO or YYYY-MM-DD)
   * @returns Date string for HTML input (YYYY-MM-DD), or empty string
   */
  const prepareDateForInput = useCallback((isoString: string | null | undefined): string => {
    if (!isoString) return '';

    try {
      // Extract just the date part (YYYY-MM-DD)
      const datePart = isoString.split('T')[0];

      // Validate format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        console.warn('Invalid date format for input:', isoString);
        return '';
      }

      return datePart;
    } catch (error) {
      console.error('Error preparing date for input:', isoString, error);
      return '';
    }
  }, []);

  /**
   * Formata uma data relativa (ex: "2 dias atrás", "em 3 horas")
   *
   * Useful for showing relative time for events
   *
   * Examples:
   *   Today at 10:00     → 'Hoje às 10:00'
   *   Yesterday at 15:00 → 'Ontem às 15:00'
   *   2 days ago         → 'Há 2 dias'
   *   In 3 days          → 'Em 3 dias'
   *
   * @param isoString - ISO timestamp string
   * @returns Relative time string in Portuguese
   */
  const formatRelativeTime = (isoString: string | null | undefined): string => {
    if (!isoString) return '-';

    try {
      // Parse ISO string to Date object (UTC)
      const utcDate = parseISO(isoString);

      // Convert UTC to user's timezone for calculations
      const zonedDate = toZonedTime(utcDate, userTimezone);
      const now = toZonedTime(new Date(), userTimezone);

      const diffMs = now.getTime() - zonedDate.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        // Today - format time in user's timezone
        return `Hoje às ${formatInTimeZone(utcDate, userTimezone, 'HH:mm')}`;
      } else if (diffDays === 1) {
        // Yesterday
        return `Ontem às ${formatInTimeZone(utcDate, userTimezone, 'HH:mm')}`;
      } else if (diffDays === -1) {
        // Tomorrow
        return `Amanhã às ${formatInTimeZone(utcDate, userTimezone, 'HH:mm')}`;
      } else if (diffDays > 1 && diffDays < 7) {
        // Within last week
        return `Há ${diffDays} dias`;
      } else if (diffDays < -1 && diffDays > -7) {
        // Within next week
        return `Em ${Math.abs(diffDays)} dias`;
      } else {
        // Older/further - show full date in user's timezone
        return formatInTimeZone(utcDate, userTimezone, 'dd/MM/yyyy', { locale: ptBR });
      }
    } catch (error) {
      console.error('Error formatting relative time:', isoString, error);
      return '-';
    }
  };

  /**
   * Valida se uma string é uma data válida
   *
   * @param dateString - String to validate
   * @returns true if valid date, false otherwise
   */
  const isValidDate = (dateString: string | null | undefined): boolean => {
    if (!dateString) return false;

    try {
      const date = new Date(dateString);
      return !isNaN(date.getTime());
    } catch {
      return false;
    }
  };

  /**
   * Retorna a data atual no formato YYYY-MM-DD
   * Útil para valores padrão em inputs
   *
   * @returns Current date string (YYYY-MM-DD)
   */
  const getCurrentDate = (): string => {
    const now = new Date();
    return format(now, 'yyyy-MM-dd');
  };

  /**
   * Formata um valor numérico como moeda brasileira (BRL)
   *
   * Examples:
   *   1234.56    → 'R$ 1.234,56'
   *   1000       → 'R$ 1.000,00'
   *   0          → 'R$ 0,00'
   *   null       → 'R$ 0,00'
   *
   * @param value - Numeric value to format
   * @returns Formatted currency string
   */
  const formatCurrency = (value: number | string | null | undefined): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (numValue === null || numValue === undefined || isNaN(numValue)) {
      return 'R$ 0,00';
    }

    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(numValue);
  };

  return {
    userTimezone,
    formatDateOnly,
    formatDateTime,
    formatDateTimeShort,
    formatRelativeTime,
    prepareDateForSubmit,
    prepareDateForInput,
    isValidDate,
    getCurrentDate,
    formatCurrency
  };
}
